<script setup lang="ts">
import type { BackgroundSettings, AudioSettings, ThemeSettings, TextSettings, WidgetPosition } from '~/stores/auth'
import type { CharacterPageData, CharacterPageSettings, AudioPlaylistItem } from '~/composables/useCharacterPage'
import { useLayoutStyles } from '~/composables/useLayoutStyles'
import { useAudioPlayer } from '~/composables/useAudioPlayer'

const props = defineProps<{
  character?: CharacterPageData | null
  settings: CharacterPageSettings
  backgroundSettings: BackgroundSettings
  audioSettings: AudioSettings
  themeSettings: ThemeSettings
  textSettings: TextSettings
  audioPlaylist: AudioPlaylistItem[]
  totalViews?: number
  isOwner: boolean
  editMode?: boolean
}>()

const emit = defineEmits<{
  'update:widgetPosition': [widget: 'audio' | 'views' | 'comments', position: WidgetPosition]
  'widget-drag-start': []
  'open-comment-settings': []
}>()

const { themeVars, bgGradientStyle, brandRgb } = useLayoutStyles({
  themeSettings: toRef(props, 'themeSettings'),
  textSettings: computed(() => ({ color: '#FDFDFB', fontScale: 1, fontWeight: '400', fontFamily: 'Rajdhani' })),
  settings: toRef(props, 'settings')
})

// Shared audio player state - provided to AudioPlayer (desktop) and MobileBottomBar
const hasAudio = computed(() => props.audioPlaylist.length > 0)
const audioPlayer = hasAudio.value
  ? useAudioPlayer(
      computed(() => props.audioPlaylist),
      computed(() => props.audioSettings)
    )
  : null

if (audioPlayer) {
  provide('audioPlayer', audioPlayer)
}

// Defer card animations until the user has "entered" the page (clicked through the audio overlay)
// Pages without audio are considered entered immediately
const pageEntered = computed(() => !hasAudio.value || (audioPlayer?.pageEntered.value ?? true))
provide('pageEntered', pageEntered)

// Widget corner positions (from unified widgets settings)
const widgetSettings = computed(() => props.settings.widgets)
const audioPosition = computed(() => widgetSettings.value.audio.position)
const viewsPosition = computed(() => widgetSettings.value.views.position)
const commentsPosition = computed(() => widgetSettings.value.comments.position)

const activeCorners = computed(() => {
  const map: Record<WidgetPosition, { audio: boolean, views: boolean, comments: boolean }> = {
    'bottom-left': { audio: false, views: false, comments: false },
    'bottom-right': { audio: false, views: false, comments: false },
    'top-left': { audio: false, views: false, comments: false },
    'top-right': { audio: false, views: false, comments: false }
  }
  if (hasAudio.value && widgetSettings.value.audio.visible) map[audioPosition.value].audio = true
  if (widgetSettings.value.views.visible && props.totalViews !== undefined) map[viewsPosition.value].views = true
  if (widgetSettings.value.comments.visible) map[commentsPosition.value].comments = true
  return Object.entries(map).filter(([_, v]) => v.audio || v.views || v.comments) as [WidgetPosition, { audio: boolean, views: boolean, comments: boolean }][]
})

// Widget drag-to-move (edit mode only) - uses VueUse pointer tracking
const { x: pointerX, y: pointerY } = usePointer()
const { width: windowW, height: windowH } = useWindowSize()

const movingWidget = ref<'audio' | 'views' | 'comments' | null>(null)

const dropCorners: { position: WidgetPosition, label: string, icon: string }[] = [
  { position: 'top-left', label: 'Top Left', icon: 'i-lucide-arrow-up-left' },
  { position: 'top-right', label: 'Top Right', icon: 'i-lucide-arrow-up-right' },
  { position: 'bottom-left', label: 'Bottom Left', icon: 'i-lucide-arrow-down-left' },
  { position: 'bottom-right', label: 'Bottom Right', icon: 'i-lucide-arrow-down-right' }
]

// Reactively compute which corner the pointer is over based on screen quadrant
const targetCorner = computed<WidgetPosition | null>(() => {
  if (!movingWidget.value) return null
  const isLeft = pointerX.value < windowW.value / 2
  const isTop = pointerY.value < windowH.value / 2
  if (isTop && isLeft) return 'top-left'
  if (isTop && !isLeft) return 'top-right'
  if (!isTop && isLeft) return 'bottom-left'
  return 'bottom-right'
})

function onWidgetPointerDown(e: PointerEvent, widget: 'audio' | 'views' | 'comments') {
  e.preventDefault()
  e.stopPropagation()
  movingWidget.value = widget
  emit('widget-drag-start')
}

function onOverlayPointerUp() {
  if (!movingWidget.value || !targetCorner.value) return
  emit('update:widgetPosition', movingWidget.value, targetCorner.value)
  movingWidget.value = null
}

function cancelWidgetMove() {
  movingWidget.value = null
}

useEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape' && movingWidget.value) cancelWidgetMove()
})

// Custom cursor - disabled on touch devices (mobile)
const isTouchDevice = ref(false)
const hasCursor = computed(() => !!props.settings.cursor_url && !isTouchDevice.value)
const hasTrail = computed(() => !!props.settings.cursor?.trail && props.settings.cursor.trail !== 'none' && !isTouchDevice.value)
const cursorX = ref(0)
const cursorY = ref(0)
const cursorVisible = ref(false)

function onMouseMove(e: MouseEvent) {
  if (!hasCursor.value && !hasTrail.value) return
  cursorX.value = e.clientX
  cursorY.value = e.clientY
  if (!cursorVisible.value) cursorVisible.value = true
}

function onMouseLeave() {
  cursorVisible.value = false
}

// Branding card - hidden for paid plans (branding === false). Rendered
// server-side (it is the page's only internal link, crawlers need it in the
// SSR HTML); the entrance animation runs via the Transition's `appear`.
const showBranding = computed(() => props.settings.branding !== false)

onMounted(() => {
  isTouchDevice.value = window.matchMedia('(pointer: coarse)').matches
})
</script>

<template>
  <div
    :class="{ 'custom-cursor-active': hasCursor }"
    :style="themeVars"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <!-- Custom cursor -->
    <div
      v-if="hasCursor && cursorVisible"
      class="fixed pointer-events-none z-9999"
      :style="{
        left: `${cursorX}px`,
        top: `${cursorY}px`,
        width: '32px',
        height: '32px'
      }"
    >
      <img
        :src="settings.cursor_url!"
        class="w-full h-full object-contain"
        draggable="false"
      >
    </div>

    <!-- Cursor trail canvas (below cursor image) -->
    <CursorTrailCanvas
      v-if="hasTrail"
      :trail="settings.cursor!.trail"
      :mouse-x="cursorX"
      :mouse-y="cursorY"
      :mouse-active="cursorVisible"
      :primary-color="themeSettings.primaryColor"
    />

    <!-- Background -->
    <div class="fixed inset-0 z-0">
      <!-- Default gradient (base layer) -->
      <div
        class="absolute inset-0"
        :style="bgGradientStyle"
      />
      <!-- Background video -->
      <video
        v-if="settings.background_url && settings.background_type === 'video'"
        :src="settings.background_url"
        autoplay
        muted
        loop
        playsinline
        class="absolute inset-0 w-full h-full object-cover"
        :style="{
          objectPosition: `${backgroundSettings.positionX ?? 50}% ${backgroundSettings.positionY ?? 50}%`,
          opacity: backgroundSettings.opacity / 100,
          filter: backgroundSettings.blur > 0 ? `blur(${backgroundSettings.blur}px)` : undefined,
          transform: (backgroundSettings.scale ?? 100) !== 100 ? `scale(${(backgroundSettings.scale ?? 100) / 100})` : undefined,
          transformOrigin: `${backgroundSettings.positionX ?? 50}% ${backgroundSettings.positionY ?? 50}%`
        }"
      />
      <!-- Background image -->
      <div
        v-else-if="settings.background_url"
        class="absolute inset-0 bg-no-repeat"
        :style="{
          backgroundImage: `url(${settings.background_url})`,
          backgroundSize: 'cover',
          backgroundPosition: `${backgroundSettings.positionX ?? 50}% ${backgroundSettings.positionY ?? 50}%`,
          opacity: backgroundSettings.opacity / 100,
          filter: backgroundSettings.blur > 0 ? `blur(${backgroundSettings.blur}px)` : undefined,
          transform: (backgroundSettings.scale ?? 100) !== 100 ? `scale(${(backgroundSettings.scale ?? 100) / 100})` : undefined,
          transformOrigin: `${backgroundSettings.positionX ?? 50}% ${backgroundSettings.positionY ?? 50}%`
        }"
      />
      <!-- Effect canvas -->
      <BackgroundEffectCanvas
        :effect="backgroundSettings.effect"
      />
      <!-- Gradient color overlay -->
      <div
        v-if="backgroundSettings.gradientColor"
        class="absolute inset-0"
        :style="{
          background: `linear-gradient(to top, ${backgroundSettings.gradientColor} 0%, transparent 100%)`,
          opacity: backgroundSettings.gradientOpacity / 100
        }"
      />
    </div>

    <!-- Content (slot for layout component) -->
    <div class="relative z-1 pb-16 sm:pb-0">
      <slot />
    </div>

    <!-- Autoplay blocked overlay (full-screen, both mobile and desktop) -->
    <Transition name="fade">
      <div
        v-if="audioPlayer?.autoplayBlocked.value"
        class="fixed inset-0 z-9998 flex items-center justify-center cursor-pointer"
        @click="audioPlayer.enterPage"
      >
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div class="relative flex flex-col items-center gap-5">
          <div class="size-20 rounded-full flex items-center justify-center bg-primary-500/15 border border-primary-400/25 text-primary-300 enter-pulse">
            <UIcon
              name="i-lucide-play"
              class="size-8 ml-1"
            />
          </div>
          <div class="text-center">
            <p class="text-white/80 text-lg font-medium tracking-wide">
              {{ audioSettings.enterLabel || 'Click to Enter' }}
            </p>
            <button
              class="text-white/30 hover:text-white/50 text-xs mt-1 underline transition-colors"
              @click.stop="audioPlayer!.enterWithoutAudio"
            >
              Enter without audio
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Desktop: widgets grouped by corner (hidden on mobile via WidgetCorner) -->
    <WidgetCorner
      v-for="[corner, widgets] in activeCorners"
      :key="corner"
      :position="corner"
    >
      <!-- Views Counter widget (order-1 = top of stack) -->
      <div
        v-if="widgets.views"
        class="relative order-1"
        :class="editMode ? 'group/widget' : ''"
      >
        <ViewsCounter
          :views="totalViews!"
          :text-settings="textSettings"
          :theme-settings="themeSettings"
        />
        <button
          v-if="editMode"
          class="absolute -top-2 -right-2 z-20 flex items-center justify-center size-6 rounded-full bg-black/70 border border-white/20 text-white/60 hover:text-white hover:bg-black/80 hover:scale-110 cursor-grab active:cursor-grabbing transition-all opacity-0 group-hover/widget:opacity-100"
          @pointerdown="onWidgetPointerDown($event, 'views')"
        >
          <UIcon
            name="i-lucide-move"
            class="size-3"
          />
        </button>
      </div>

      <!-- Audio Player widget (order-2 = middle) -->
      <div
        v-if="widgets.audio"
        class="relative order-2"
        :class="editMode ? 'group/widget' : ''"
      >
        <AudioPlayer
          :show-player="widgetSettings.audio.visible"
          :theme-settings="themeSettings"
        />
        <button
          v-if="editMode"
          class="absolute -top-2 -right-2 z-20 flex items-center justify-center size-7 rounded-full bg-black/70 border border-white/20 text-white/60 hover:text-white hover:bg-black/80 hover:scale-110 cursor-grab active:cursor-grabbing transition-all opacity-0 group-hover/widget:opacity-100"
          @pointerdown="onWidgetPointerDown($event, 'audio')"
        >
          <UIcon
            name="i-lucide-move"
            class="size-3.5"
          />
        </button>
      </div>

      <!-- Comments widget (order-3 = always at bottom) -->
      <div
        v-if="widgets.comments && character"
        class="relative order-3"
        :class="editMode ? 'group/widget' : ''"
      >
        <CommentsWidget
          :character="character"
          :settings="settings"
          :theme-settings="themeSettings"
          :text-settings="textSettings"
          :comment-settings="settings.comments"
          :edit-mode="editMode"
          :is-owner="isOwner"
          @open-comment-settings="emit('open-comment-settings')"
        />
        <button
          v-if="editMode"
          class="absolute -top-2 -right-2 z-20 flex items-center justify-center size-6 rounded-full bg-black/70 border border-white/20 text-white/60 hover:text-white hover:bg-black/80 hover:scale-110 cursor-grab active:cursor-grabbing transition-all opacity-0 group-hover/widget:opacity-100"
          @pointerdown="onWidgetPointerDown($event, 'comments')"
        >
          <UIcon
            name="i-lucide-move"
            class="size-3"
          />
        </button>
      </div>
    </WidgetCorner>

    <!-- Widget move overlay - dark backdrop + pointer-following ghost + corner drop zones -->
    <Transition name="fade">
      <div
        v-if="editMode && movingWidget"
        class="fixed inset-0 z-9990 hidden sm:block cursor-grabbing"
        @pointerup="onOverlayPointerUp"
        @contextmenu.prevent="cancelWidgetMove"
      >
        <!-- Dark backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

        <!-- Ghost label following pointer -->
        <div
          class="fixed z-10 pointer-events-none -translate-x-1/2 -translate-y-1/2"
          :style="{ left: `${pointerX}px`, top: `${pointerY}px` }"
        >
          <div class="px-3 py-1.5 rounded-full bg-violet-500/30 border border-violet-400/50 text-white text-xs font-medium shadow-lg backdrop-blur-sm">
            {{ movingWidget === 'audio' ? 'Audio Player' : movingWidget === 'views' ? 'Views Counter' : 'Comments' }}
          </div>
        </div>

        <!-- Instruction -->
        <div class="absolute top-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 border border-white/15 pointer-events-none">
          <UIcon
            name="i-lucide-move"
            class="size-4 text-violet-400"
          />
          <span class="text-sm text-white/80 font-medium">Drag to a corner</span>
          <span class="text-xs text-white/40">· Release to place · Esc to cancel</span>
        </div>

        <!-- 4 corner drop zone boxes -->
        <div
          v-for="corner in dropCorners"
          :key="corner.position"
          class="fixed w-36 h-28 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all duration-200 pointer-events-none"
          :class="[
            corner.position === 'top-left' ? 'top-6 left-6' : '',
            corner.position === 'top-right' ? 'top-6 right-6' : '',
            corner.position === 'bottom-left' ? 'bottom-6 left-6' : '',
            corner.position === 'bottom-right' ? 'bottom-6 right-6' : '',
            targetCorner === corner.position
              ? 'border-violet-400 bg-violet-500/25 scale-110'
              : 'border-white/20 bg-white/5'
          ]"
        >
          <UIcon
            :name="corner.icon"
            class="size-5 transition-colors duration-200"
            :class="targetCorner === corner.position ? 'text-violet-300' : 'text-white/40'"
          />
          <span
            class="text-xs font-medium transition-colors duration-200"
            :class="targetCorner === corner.position ? 'text-violet-200' : 'text-white/40'"
          >
            {{ corner.label }}
          </span>
        </div>
      </div>
    </Transition>

    <!-- Desktop branding card (hidden on mobile) -->
    <Transition
      name="brand-slide"
      appear
    >
      <NuxtLink
        v-if="!editMode && showBranding"
        :to="isOwner ? '/pricing' : '/'"
        class="hidden sm:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-40 group items-center gap-3 px-5 py-2.5 rounded-full border backdrop-blur-md transition-all duration-300 hover:scale-105 cursor-pointer"
        :style="{
          background: `linear-gradient(135deg, rgba(${brandRgb.join(',')}, 0.15) 0%, rgba(${brandRgb.join(',')}, 0.06) 100%)`,
          borderColor: `rgba(${brandRgb.join(',')}, 0.25)`,
          boxShadow: `0 0 24px rgba(${brandRgb.join(',')}, 0.2), 0 4px 16px rgba(0, 0, 0, 0.3)`
        }"
      >
        <span
          class="text-xs font-bold tracking-wide"
          :style="{ color: `color-mix(in srgb, ${themeSettings.primaryColor} 50%, white)` }"
        >
          mychar.gg
        </span>
        <img
          src="/logo_short_transparent_white.png"
          alt="mychar logo"
          class="size-4 group-hover:scale-110 transition-transform duration-200"
        >
        <span
          v-if="isOwner"
          class="text-[11px] font-medium text-white/60 group-hover:text-white/80 transition-colors"
        >
          Remove Branding
        </span>
        <span
          v-else
          class="text-[11px] font-medium text-white/60 group-hover:text-white/80 transition-colors"
        >
          Create Yours
        </span>
      </NuxtLink>
    </Transition>

    <!-- Mobile: consolidated bottom bar -->
    <MobileBottomBar
      v-if="!editMode"
      :has-audio="hasAudio"
      :show-player="widgetSettings.audio.visible"
      :show-branding="showBranding"
      :is-owner="isOwner"
      :show-views="widgetSettings.views.visible"
      :total-views="totalViews"
      :theme-settings="themeSettings"
    >
      <template #fabs>
        <slot name="mobile-fabs" />
      </template>
    </MobileBottomBar>
  </div>
</template>

<style>
.custom-cursor-active,
.custom-cursor-active * {
  cursor: none !important;
}

.brand-slide-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 1s;
}
.brand-slide-leave-active {
  transition: all 0.3s ease-in;
}
.brand-slide-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
.brand-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.enter-pulse {
  animation: enter-pulse 2s ease-in-out infinite;
}

@keyframes enter-pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
